import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '@/context/AuthContext';
import { addToCart, addToWishlist, getCartCount, getWishlist, removeFromWishlist } from '@/api/shop';
import { friendlyError } from '@/lib/apiError';

/**
 * Tiny app-wide store for the two pieces of shop state the header and every
 * product card need: the cart badge count and which products are wishlisted.
 * A module-level store (useSyncExternalStore) rather than a context, so no
 * provider has to be threaded through App.jsx.
 */
let state = { cartCount: 0, wishlist: null, freeDeliveryAbove: null };
const listeners = new Set();

const set = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
};
const subscribe = (l) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const snapshot = () => state;

export const useShopState = () => useSyncExternalStore(subscribe, snapshot, snapshot);

/**
 * Any cart view from the API carries `count`; feed it here to keep the badge in
 * step. Its summary also carries the free-delivery threshold, which product
 * pages quote.
 */
export const syncCartCount = (view) => {
  const patch = {};
  if (view && typeof view.count === 'number') patch.cartCount = view.count;
  const free = Number(view?.summary?.freeDeliveryAbove);
  if (Number.isFinite(free) && free >= 0 && view?.summary?.freeDeliveryAbove != null) patch.freeDeliveryAbove = free;
  if (Object.keys(patch).length) set(patch);
};

/* ------------------------------------------------- guest → login → resume */

/**
 * A guest who taps Add to cart / Buy now / the heart / Checkout is asked to log
 * in. What they tapped is parked in sessionStorage and done once the login
 * lands, so they are not left to find the button again. Shapes:
 *   { type: 'cart', listingId, qty }      add, then stay where they are
 *   { type: 'wishlist', productId, variantId }
 *   { type: 'go', to }                    buy now / checkout: open that page
 * Closing the login without signing in drops it.
 */
const PENDING_KEY = 'lk-shop-pending';
const PENDING_TTL_MS = 30 * 60 * 1000;

export const rememberShopAction = (action) => {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ ...action, at: Date.now() }));
  } catch {
    // Private mode / storage off: the login still works, the tap just isn't replayed.
  }
};

const clearShopAction = () => {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
};

const takeShopAction = () => {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    sessionStorage.removeItem(PENDING_KEY);
    const a = raw ? JSON.parse(raw) : null;
    return a && Date.now() - Number(a.at || 0) < PENDING_TTL_MS ? a : null;
  } catch {
    return null;
  }
};

const runShopAction = async (action, navigate) => {
  if (!action) return;
  try {
    if (action.type === 'cart' && action.listingId) {
      const view = await addToCart(Number(action.listingId), Number(action.qty) || 1);
      syncCartCount(view);
      toast.success('Added to cart', { id: 'added' });
    } else if (action.type === 'wishlist' && action.productId) {
      await addToWishlist(action.productId, action.variantId);
      toast.success('Saved to wishlist', { id: `wish-${action.productId}` });
    } else if (action.type === 'go' && typeof action.to === 'string' && action.to.startsWith('/shop')) {
      navigate(action.to);
    }
  } catch (err) {
    toast.error(friendlyError(err, action.type === 'wishlist' ? 'Could not update your wishlist' : 'Could not add to cart'));
  }
};

export const refreshCartCount = async () => {
  try {
    set({ cartCount: await getCartCount() });
  } catch {
    // The badge is a hint; a failed count must never break the page.
  }
};

export const refreshWishlist = async () => {
  try {
    const items = await getWishlist();
    set({ wishlist: new Set((items || []).map((p) => p.id)) });
    return items || [];
  } catch {
    return null;
  }
};

/**
 * Mounted once (by the header): loads the badge and wishlist when someone is
 * signed in, and clears them on sign-out so the next visitor sees nothing.
 */
export const useShopSession = () => {
  const { user, isAuthModalOpen } = useAuth();
  const navigate = useNavigate();
  const userId = user?.id ?? null;
  useEffect(() => {
    if (!userId) {
      set({ cartCount: 0, wishlist: null });
      return;
    }
    // Finish what a guest tapped before logging in, then load the badge and
    // hearts (after, so they include it).
    runShopAction(takeShopAction(), navigate).finally(() => {
      refreshCartCount();
      refreshWishlist();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Login closed without signing in: forget the parked tap.
  const wasOpen = useRef(isAuthModalOpen);
  useEffect(() => {
    if (wasOpen.current && !isAuthModalOpen && !userId) clearShopAction();
    wasOpen.current = isAuthModalOpen;
  }, [isAuthModalOpen, userId]);
};

/** Ask a guest to log in, then do `action` (see rememberShopAction) once they have. */
export const useLoginThen = () => {
  const { openAuthModal } = useAuth();
  return useCallback(
    (action) => {
      if (action) rememberShopAction(action);
      openAuthModal();
    },
    [openAuthModal]
  );
};

/** Heart toggle: optimistic, rolls back on failure, asks guests to log in. */
export const useWishlistToggle = () => {
  const { user } = useAuth();
  const loginThen = useLoginThen();
  const { wishlist } = useShopState();
  const toggle = useCallback(
    async (productId, variantId) => {
      if (!user) {
        loginThen({ type: 'wishlist', productId, variantId });
        return;
      }
      const had = state.wishlist?.has(productId);
      const next = new Set(state.wishlist || []);
      if (had) next.delete(productId);
      else next.add(productId);
      set({ wishlist: next });
      try {
        if (had) await removeFromWishlist(productId);
        else await addToWishlist(productId, variantId);
        toast.success(had ? 'Removed from wishlist' : 'Saved to wishlist', { id: `wish-${productId}` });
      } catch (err) {
        const back = new Set(state.wishlist || []);
        if (had) back.add(productId);
        else back.delete(productId);
        set({ wishlist: back });
        toast.error(friendlyError(err, 'Could not update your wishlist'));
      }
    },
    [user, loginThen]
  );
  return { wishlist, toggle };
};
